import React from 'react';

import { Box, Typography } from '@mui/material';

import { SeatFeature, SeatInfo, SeatMapRow, SeatSelectionState, Traveller } from '../charter.types';
import { formatTravellerName } from '../common/formatTravellerName';
import { getSeatFeatureDescriptor } from '../common/getSeatFeatureDescriptor';
import { SEAT_FEATURE_CODE_ALIASES } from '../common/seatFeatureAliases';

import { BUSINESS_SEAT_WIDTH, ECONOMY_SEAT_SIZE, SEAT_HEIGHT } from './seatConstants';

type SeatTileProps = {
  seatId: string;
  row: SeatMapRow;
  flightId: string;
  seatSelections: SeatSelectionState;
  travellers: Array<Traveller>;
  displayLabel?: string;
  columnSpan?: number;
};

export default function SeatTile({
  seatId,
  row,
  flightId,
  seatSelections,
  travellers,
  displayLabel,
  columnSpan = 1,
}: SeatTileProps): React.JSX.Element {
  const seat = row.seats.find((s) => s.seatId === seatId);
  const occupant = findSeatOccupant(seat?.primaryColumn ?? seatId, flightId, seatSelections, travellers);
  const isSelected = Boolean(occupant);
  const isSeatAvailable = seat ? seat.isAvailable && !isSelected : false;
  const seatWidth = (row.cabin === 'business' ? BUSINESS_SEAT_WIDTH : ECONOMY_SEAT_SIZE) * Math.max(1, columnSpan);
  const featureCodes = seat ? getSeatFeatureCodes(seat.feature) : [];
  const featureDescription = seat ? getSeatFeatureLabel(seat.feature) : null;
  const priceLabel = seat?.price ? `$${seat.price}` : 'Free';
  const label = displayLabel ?? seat?.displayLabel ?? seatId;

  return (
    <Box
      sx={{
        bgcolor: getSeatDisplayColor(seat, isSelected, isSeatAvailable),
        border: '1px solid',
        borderColor: isSeatAvailable ? 'divider' : 'transparent',
        borderRadius: 1,
        color: isSelected ? 'common.white' : isSeatAvailable ? 'text.primary' : 'text.disabled',
        width: seatWidth,
        height: SEAT_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.25,
        py: 0.5,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        gridColumn: columnSpan > 1 ? `span ${columnSpan}` : undefined,
      }}
      title={[
        `Seat ${label}`,
        featureDescription,
        priceLabel,
        !seat?.isAvailable ? 'Unavailable' : null,
        occupant ? `Assigned to: ${formatTravellerName(occupant)}` : null,
      ]
        .filter(Boolean)
        .join(' • ')}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: 12,
          lineHeight: 1.1,
          fontWeight: 600,
          opacity: 0.85,
        }}
      >
        {priceLabel}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          fontSize: 16,
          lineHeight: 1.1,
          my: 0.2,
        }}
      >
        {label}
      </Typography>
      {featureCodes.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            fontSize: 12,
            lineHeight: 1,
            fontWeight: 500,
            opacity: 0.75,
            letterSpacing: 0.2,
          }}
        >
          {featureCodes.join(' ')}
        </Typography>
      )}
    </Box>
  );
}

const findSeatOccupant = (
  seatId: string,
  flightId: string,
  seatSelections: SeatSelectionState,
  travellers: Array<Traveller>,
): Traveller | null => {
  const byTraveller = seatSelections[flightId] ?? {};
  const entry = Object.entries(byTraveller).find(([, selectedSeat]) => selectedSeat === seatId);
  if (!entry) {
    return null;
  }

  const [travellerId] = entry;
  return travellers.find((traveller) => traveller.id === travellerId) ?? null;
};

const getSeatFeatureCodes = (feature: SeatFeature): Array<string> =>
  (Object.keys(SEAT_FEATURE_CODE_ALIASES) as Array<keyof SeatFeature>)
    .filter((key) => feature[key])
    .map((key) => SEAT_FEATURE_CODE_ALIASES[key]);

const getSeatFeatureLabel = (feature: SeatFeature) => getSeatFeatureDescriptor(feature).label;
const getSeatDisplayColor = (seat: SeatInfo | undefined, isSelected: boolean, isAvailable: boolean) => {
  if (!seat) {
    return 'transparent';
  }

  if (isSelected) {
    return '#1e88e5';
  }

  if (!isAvailable) {
    return '#a4abae';
  }

  return getSeatFeatureDescriptor(seat.feature).color;
};
